import { useMemo } from 'react';
import type { TableData } from '@/components/custom/erp/data-table-v2';
import { BangKeCongNoTheoHoaDonItem } from '@/types/schemas';

export interface UseTableDataReturn {
  tables: TableData[];
  handleRowClick: (params: MuaHangRecord) => void;
}

export function useTableData(data: BangKeCongNoTheoHoaDonItem[], searchParams?: MuaHangRecord): UseTableDataReturn {
  const tables = useMemo(() => {
    // Calculate totals for financial columns
    const calculateTotals = (items: BangKeCongNoTheoHoaDonItem[]) => {
      return items.reduce(
        (totals, item) => ({
          t_tt: totals.t_tt + (item.t_tt || 0),
          tt: totals.tt + (item.tt || 0),
          cl: totals.cl + (item.cl || 0),
          trong_han: totals.trong_han + (item.trong_han || 0),
          qua_han01: totals.qua_han01 + (item.qua_han01 || 0),
          qua_han02: totals.qua_han02 + (item.qua_han02 || 0),
          qua_han03: totals.qua_han03 + (item.qua_han03 || 0),
          qua_han04: totals.qua_han04 + (item.qua_han04 || 0),
          qua_han05: totals.qua_han05 + (item.qua_han05 || 0)
        }),
        {
          t_tt: 0,
          tt: 0,
          cl: 0,
          trong_han: 0,
          qua_han01: 0,
          qua_han02: 0,
          qua_han03: 0,
          qua_han04: 0,
          qua_han05: 0
        }
      );
    };

    const totals = calculateTotals(data);
    const ct_theo = searchParams?.ct_theo;

    // Base columns that are always shown
    const baseColumns = [
      {
        field: 'stt',
        headerName: 'Stt',
        width: 100,
        cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
      }
    ];

    // Columns for ct_theo = '1' (invoice-based view)
    const invoiceColumns =
      ct_theo === '1'
        ? [
            {
              field: 'ngay_ct',
              headerName: 'Ngày c/từ',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'so_ct',
              headerName: 'Số c/từ',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'ngay_ct0',
              headerName: 'Ngày hoá đơn',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'so_ct0',
              headerName: 'Số hoá đơn',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'ma_kh',
              headerName: 'Mã NCC',
              width: 100,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'ten_kh',
              headerName: 'Tên nhà cung cấp',
              width: 200,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 't_tt',
              headerName: 'Tổng tiền HĐ',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'tt',
              headerName: 'Đã trả',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'cl',
              headerName: 'Phải trả',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'trong_han',
              headerName: 'Trong hạn',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han01',
              headerName: 'Quá hạn 1-30 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han02',
              headerName: 'Quá hạn 31-60 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han03',
              headerName: 'Quá hạn 61-90 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han04',
              headerName: 'Quá hạn 91-120 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han05',
              headerName: 'Quá hạn trên 121 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'ngay_dh0',
              headerName: 'Ngày đến hạn',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'han_tt',
              headerName: 'Hạn tt',
              width: 120,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'so_ngay',
              headerName: 'Số ngày đến hạn',
              width: 100,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'dien_giai',
              headerName: 'Diễn giải',
              width: 200,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'ma_unit',
              headerName: 'Đơn vị',
              width: 100,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            }
          ]
        : [
            {
              field: 'ma_kh',
              headerName: 'Mã NCC',
              width: 100,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 'ten_kh',
              headerName: 'Tên nhà cung cấp',
              width: 200,
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : '')
            },
            {
              field: 't_tt',
              headerName: 'Tổng tiền HĐ',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'tt',
              headerName: 'Đã trả',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'cl',
              headerName: 'Phải trả',
              width: 100,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'trong_han',
              headerName: 'Trong hạn',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han01',
              headerName: 'Quá hạn 1-30 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han02',
              headerName: 'Quá hạn 31-60 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han03',
              headerName: 'Quá hạn 61-90 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han04',
              headerName: 'Quá hạn 91-120 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            },
            {
              field: 'qua_han05',
              headerName: 'Quá hạn trên 121 ngày',
              width: 120,
              type: 'number',
              cellClassName: (params: MuaHangRecord) => (params.row?.isTotal ? 'font-extrabold' : ''),
              renderCell: (params: MuaHangRecord) => {
                return params.value?.toLocaleString() || 0;
              }
            }
          ];

    const finalColumns = [...baseColumns, ...invoiceColumns];

    const tableData: TableData[] = [
      {
        name: '',
        columns: finalColumns,
        rows: [
          {
            id: 'total',
            stt: '',
            ngay_ct: '',
            so_ct: '',
            ngay_ct0: '',
            so_ct0: '',
            ma_kh: '',
            ten_kh: 'Tổng cộng',
            t_tt: totals.t_tt,
            tt: totals.tt,
            cl: totals.cl,
            trong_han: totals.trong_han,
            qua_han01: totals.qua_han01,
            qua_han02: totals.qua_han02,
            qua_han03: totals.qua_han03,
            qua_han04: totals.qua_han04,
            qua_han05: totals.qua_han05,
            ngay_dh0: '',
            han_tt: '',
            so_ngay: '',
            dien_giai: '',
            ma_unit: '',
            // Add other fields as empty strings to match the type
            id_tt: '',
            id_ct: '',
            loai_tt: '',
            unit_id: '',
            ma_ct: '',
            ma_tt: '',
            ma_nt: '',
            ty_gia: 0,
            t_tt_nt0: 0,
            t_tt0: 0,
            tt_yn: '',
            ngay_tt: '',
            isTotal: true // Đánh dấu đây là dòng tổng
          },
          ...data.map((item, index) => ({
            ...item,
            stt: index + 1
          }))
        ]
      }
    ];

    return tableData;
  }, [data, searchParams?.ct_theo]);

  const handleRowClick = (params: MuaHangRecord) => {
    console.log('Row clicked:', params);
  };

  return {
    tables,
    handleRowClick
  };
}
